import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT,
    PUBLISHED, UNPUBLISHED,
} from "const"
import {
    SilDevice,
    SilDeviceCreate,
    SilProjectDevice,
    SilProjectDeviceCreate,
} from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    SilDeviceValidation,
    SilDeviceNestedType,

    LoadSilDeviceById,
    LoadAccountPackageUsage,
    CalculateSubsystem,
    GetMaxSilSubsystems,
    SaveSilDevice,
    SilProjectDeviceUpdateByFilters,
    SilProjectDeviceUpdateRequired,
    UpdateSilResultProjectDevice,
    IncreaseSilCalculations,
    CountCalculatedSafetyFunctions,
    DeleteSilNestedDevices,
    AddSilNestedDevice,

    initSilDevice,
    closeModal,
    setSilDeviceEdit,
    setSilDeviceReloadList,
    setSilDeviceReloadView,
    setSilDeviceReloadEdit,
    setSystemPackageUsage,
} from "client/store"
import {
    Container,
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    FileLoader,
    RadioButton,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"

import Model from "./model"
import CalculatedData from "./calculatedData"

interface EditSubsystemProps {
    subsystem: SilDevice | SilProjectDevice
}

let isMounted: boolean

let updated = false
let recalculationRequired = false
let saveMode = 0

const Form = ({
    subsystem,
}: EditSubsystemProps) => {
    const nestedDevices = useSelector((state: StoreType) => state.silDevice.nested.newDeviceList)
    const oldNestedDevices = useSelector((state: StoreType) => state.silDevice.nested.currentDeviceList)

    const baseRoute = useSelector((state: StoreType) => state.silDevice.baseSubsystemRoute)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const packageUsage = useSelector((state: StoreType) => state.system.packageUsage)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(subsystem.name)
    const [manufacturer, setManufacturer] = useInput(subsystem.manufacturer)
    const [description, setDescription] = useInput(subsystem.description)
    const [reference, setReference] = useInput(subsystem.reference)

    const [logoId, setLogoId] = useState(subsystem.logoId)
    const [logo, setLogo] = useState(subsystem.logo)
    const [status, setStatus] = useState(subsystem.status)

    const [validation, setValidation] = useState<SilDeviceValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)
    const [recalculateCount, setRecalculateCount] = useState(0)

    const accountDevice = subsystem as SilDevice
    const projectDevice = subsystem as SilProjectDevice

    // calculated subsystem data
    const {
        calculatedSubsystem,
        disableSave,
        emptyBlocks,
    } = CalculateSubsystem(
        { ...initSilDevice, name },
        nestedDevices,
    )


    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = subsystem.id
            ? `${baseRoute}/${VIEW}/${subsystem.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onCancelConfirm = () => {
        setSaveState(IDLE)
        setApplyState(IDLE)
        setRecalculateCount(0)
    }

    const onSave = () => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        LoadSilDeviceById(subsystem.id, userData.accountId, project.id).then((existingDevice) => {
            updated = false
            recalculationRequired = false

            if (
                existingDevice && (
                    existingDevice.manufacturer !== manufacturer
                    || existingDevice.name !== name
                    || existingDevice.description !== description
                    || existingDevice.reference !== reference
                    || existingDevice.logoId !== logoId
                    || existingDevice.status !== status
                )
            ) {
                updated = true
            }

            if (existingDevice && oldNestedDevices !== nestedDevices) {
                updated = true
                if (project.id) recalculationRequired = true
            }

            if (recalculationRequired) {
                // check, if there are functions that use this subsystem
                CountCalculatedSafetyFunctions({
                    projectId: project.id,
                    deviceId: projectDevice.deviceId,
                    projectDeviceId: !projectDevice.deviceId ? projectDevice.id : null,
                }).then((count) => {
                    if (count) {
                        LoadAccountPackageUsage(userData.accountId)
                        .then((res) => {
                            dispatch(setSystemPackageUsage(res.packageUsage))
                            // show confirm
                            setRecalculateCount(count)
                        })
                    } else {
                        save()
                    }
                })
            } else {
                save()
            }
        })
    }

    const save = () => {
        const newSubsystemData: SilDeviceCreate = {
            accountId: accountDevice.accountId || userData.accountId,
            manufacturer,
            name,
            logoId,
            description,
            reference,
            nested: true,
            type: calculatedSubsystem.type,
            lowDemandMode: calculatedSubsystem.lowDemandMode,
            highDemandMode: calculatedSubsystem.highDemandMode,
            continuousMode: calculatedSubsystem.continuousMode,
            logicSolver: calculatedSubsystem.logicSolver,
            provenInUse: calculatedSubsystem.provenInUse,

            failureRate: true, // this will use calculated SFF and DC
            sff: null,
            dc: null,
            pfd: null,
            pfh: null,
            sil: null,

            sd: calculatedSubsystem.sd,
            su: calculatedSubsystem.su,
            dd: calculatedSubsystem.dd,
            du: calculatedSubsystem.du,

            status,
            copy: subsystem.copy || 0,
            updated: subsystem.updated || false, // "updated" marker is not cleared
        }

        const newProjectSubsystemData: SilProjectDeviceCreate = {
            ...newSubsystemData,
            projectId: projectDevice.projectId || project.id,
            deviceId: projectDevice.deviceId || null,

            qidProductId: null,
            qidModelId: null,
            qidSafetyFunctionId: null,
            qidOptionId: null,
            qidDatapointId: null,
            qidMethod: null,

            qidModelName: null,
            qidSafetyFunctionName: null,
            qidOptionName: null,
            qidDatapointDate: null,
        }

        const { maxSubsystems } = GetMaxSilSubsystems(packageSettings, userData)

        SaveSilDevice(
            subsystem.id,
            project.id ? newProjectSubsystemData : newSubsystemData,
            project.id ? null : maxSubsystems, // unlimited number of devices on project level
            lang,
        ).then((res) => {
            // save nested devices if they were changed
            const promiseSaveNested: Promise<any>[] = []
            if (
                !res.validation
                && (oldNestedDevices !== nestedDevices || !subsystem.id)
            ) {
                promiseSaveNested.push(
                    DeleteSilNestedDevices(project.id, subsystem.id)
                    .then(() => {
                        const promiseAddDevices: Promise<any>[] = []
                        nestedDevices.forEach((nested, index) => {
                            const newNestedData: SilDeviceNestedType = {
                                ...nested,
                                parentDeviceId: res.newDeviceId || subsystem.id,
                                order: index,
                            }
                            promiseAddDevices.push(
                                AddSilNestedDevice(project.id, newNestedData),
                            )
                        })

                        return Promise.all(promiseAddDevices)
                    }),
                )
            }

            Promise.all(promiseSaveNested).then(() => {
                // update depending devices
                const promiseUpdateDepending: Promise<any>[] = []
                if (!res.validation) {
                    // saving account subsystem
                    if (!project.id && updated) {
                        promiseUpdateDepending.push(
                            // update project subsystems imported from account
                            SilProjectDeviceUpdateByFilters({ deviceId: subsystem.id }),

                            // update project subsystems that based on this device
                            SilProjectDeviceUpdateRequired({ deviceId: subsystem.id }),
                        )
                    }

                    // saving subsystem on project level
                    if (project.id && updated) {
                        // update project subsystems that based on this device
                        if (projectDevice.deviceId) { // subsystem imported from account
                            promiseUpdateDepending.push(
                                SilProjectDeviceUpdateRequired({ deviceId: projectDevice.deviceId }),
                            )
                        }
                        if (!projectDevice.deviceId) { // subsystem created on project level
                            promiseUpdateDepending.push(
                                SilProjectDeviceUpdateRequired({ projectDeviceId: projectDevice.id }),
                            )
                        }
                    }

                    // saving project subsystem
                    if (project.id) {
                        promiseUpdateDepending.push(
                            UpdateSilResultProjectDevice({
                                projectId: project.id,
                                deviceId: projectDevice.deviceId,
                                projectDeviceId: !projectDevice.deviceId ? projectDevice.id : null,
                            }),
                        )

                        if (recalculationRequired) {
                            promiseUpdateDepending.push(
                                IncreaseSilCalculations(userData.accountId, dispatch),
                            )
                        }
                    }
                }

                Promise.all(promiseUpdateDepending).then(() => {
                    if (isMounted) {
                        setValidation(res.validation)
                        setSaveState(IDLE)
                        setApplyState(IDLE)

                        if (res.newDeviceId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newDeviceId}`)
                    }
                    if (res.validation) return

                    const updatedSubsystem: SilDevice = {
                        ...newSubsystemData,
                        id: res.newDeviceId || subsystem.id,
                        logo,
                    }
                    const updatedProjectSubsystem: SilProjectDevice = {
                        ...newProjectSubsystemData,
                        id: res.newDeviceId || subsystem.id,
                        logo,
                    }
                    dispatch(setSilDeviceEdit(project.id ? updatedProjectSubsystem : updatedSubsystem))

                    dispatch(setSilDeviceReloadList(false))
                    dispatch(setSilDeviceReloadView(false))
                    setTimeout(() => {
                        dispatch(setSilDeviceReloadList(true))
                        dispatch(setSilDeviceReloadView(true))
                    }, 0)

                    if (saveMode === 1) onClose()
                    if (saveMode === 2) {
                        dispatch(setSilDeviceReloadEdit(false))
                        setTimeout(() => {
                            dispatch(setSilDeviceReloadEdit(true))
                        }, 0)
                    }
                })
            })
        })
    }


    const { maxCalculations, usedCalculations } = packageUsage

    let leftCalculations = ""
    if (maxCalculations === null) leftCalculations = lang.unlimitedCalculations
    else leftCalculations = lang.leftCalculations.replace("{0}", usedCalculations.toString()).replace("{1}", maxCalculations.toString())

    return (
        <Container>
            <PageForm>
                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            <Label>{lang.name}</Label>
                            <Input type="text" value={name} onChange={setName} />
                            <Feedback>{validation && validation.name}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.manufacturer}</Label>
                            <Input type="text" value={manufacturer} onChange={setManufacturer} />
                            <Feedback>{validation && validation.manufacturer}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.description}</Label>
                            <Textarea value={description} onChange={setDescription} />
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.image}</Label>
                            <FileLoader
                                value={logo}
                                callback={(file) => {
                                    setLogoId(file.id)
                                    setLogo(file)
                                }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.reference}</Label>
                            <Input type="text" value={reference} onChange={setReference} />
                            <Feedback>{validation && validation.reference}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.status}</Label>
                            <FormGroup inline>
                                <RadioButton
                                    checked={status === PUBLISHED}
                                    onChange={() => setStatus(PUBLISHED)}
                                    title={lang.published}
                                />
                                <RadioButton
                                    checked={status === UNPUBLISHED}
                                    onChange={() => setStatus(UNPUBLISHED)}
                                    title={lang.unpublished}
                                />
                            </FormGroup>
                        </FormGroup>
                    </Col>
                </Row>
            </PageForm>

            <Model />

            <CalculatedData
                existingSubsystem={subsystem}
                calculatedSubsystem={calculatedSubsystem}
            />

            <PageForm>
                <Row>
                    <Col xs={12}>
                        <Button
                            variant="success"
                            row
                            disabled={disableSave}
                            onClick={() => {
                                saveMode = 1
                                onSave()
                            }}
                        >
                            {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                        </Button>
                        <Button
                            variant="success"
                            row
                            disabled={disableSave}
                            onClick={() => {
                                saveMode = 2
                                onSave()
                            }}
                        >
                            {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                        </Button>
                        <Button row onClick={onClose}>{lang.cancel}</Button>
                    </Col>
                </Row>

                { emptyBlocks && <Feedback>{lang.emptyBlocksNotAllowed}</Feedback> }
                { !nestedDevices.length && <Feedback>{lang.noDeviceAdded}</Feedback> }
                { disableSave && nestedDevices.length && !emptyBlocks && <Feedback>{lang.subsystemCantBeSaved}</Feedback> }
            </PageForm>

            <ConfirmWrapper
                show={recalculateCount > 0}
                onClose={onCancelConfirm}
            >
                <Confirm align="center">
                    <ConfirmTitle>{lang.recalculationTitle}</ConfirmTitle>
                    <ConfirmDescription align="start">
                        {lang.recalculationDescription.replace("{0}", recalculateCount.toString())}
                    </ConfirmDescription>
                    <ConfirmDescription align="start">{leftCalculations}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={onCancelConfirm}>{lang.cancel}</Button>
                        <Button
                            variant="success"
                            row
                            disabled={maxCalculations !== null && usedCalculations >= maxCalculations}
                            onClick={() => {
                                save()
                                setRecalculateCount(0)
                            }}
                        >
                            {lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Container>
    )
}
export default Form
